import React from 'react';

import { useRouter } from 'next/router';
import Head from 'next/head';
import getConfig from 'next/config';

import PropTypes from 'prop-types';

const { publicRuntimeConfig } = getConfig();

const { APP_ENV } = publicRuntimeConfig;

const blackListedRoutesForIndexing = [
  '/plans',
  '/qualification-sf',
  '/accounts',
  '/payment-link',
  '/application',
  '/checkout',
  '/search',
  '/agents',
  '/benefits',
  '/verify-email',
  '/asuransi-bisnis',
  '/thank-you',
  '/payment-invalid',
  '/penawaran',
  '/compare',
  '/bengkel-mobil',
];

const Meta = props => {

  const route = useRouter();

  const isBlackListedRoute = (pathName) => {
    return blackListedRoutesForIndexing.some(blackListedRoute => pathName.startsWith(blackListedRoute));
  };

  const isBengkel = route.asPath === '/bengkel-mobil/'

  return (
    <Head>
      <title>{props.title}</title>
      <meta charSet="utf-8" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta
        name="viewport"
        content="width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=1,user-scalable=no"
      />
      <meta name="theme-color" content="#1D66DD" />
      <meta name="mobile-web-app-capable" content="yes" />
      {
        (APP_ENV !== 'production' || isBlackListedRoute(route.pathname)) && (
          <meta name="robots" content="noindex, nofollow" />
        )
      }
      {props.meta}
      {props.isPayment && !isBengkel && (
        <>
          <script type="text/javascript" src="https://cdnjs.cloudflare.com/ajax/libs/picomodal/3.0.0/picoModal.js" />
          <script type="text/javascript" src="https://js.xendit.co/v1/xendit.min.js" />
        </>
      )}
      <script type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html:
            JSON.stringify({
              "@context": "https://schema.org",
              "@graph": [
                {
                  "@type": "WebSite",
                  "@id": "https://lifepal.co.id/#website",
                  "url": "https://lifepal.co.id/",
                  "name": "Lifepal",
                  "alternateName": "Lifepal Indonesia",
                  "description": "Lifepal adalah marketplace asuransi mobil dan kesehatan di Indonesia yang memudahkanmu membandingkan polis terbaik secara online dengan premi terjangkau.",
                  "inLanguage": "id",
                  "potentialAction": {
                    "@type": "SearchAction",
                    "target": "https://lifepal.co.id/search/?s={search_term_string}",
                    "query-input": "required name=search_term_string"
                  }
                },
              ]
            })
        }}
      />
      <script type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html:
            JSON.stringify({
              "@context": "https://schema.org",
              "@graph": [
                {
                  "@type": "Organization",
                  "@id": "https://lifepal.co.id/#organization",
                  "name": "Lifepal",
                  "url": "https://lifepal.co.id/",
                  "logo": {
                    "@type": "ImageObject",
                    "@id": "https://lifepal.co.id/#organization-logo",
                    "url": "https://lifepal.co.id/media/static/images/lifepal-logo.svg",
                    "width": 93,
                    "height": 32
                  }, 
                  "image": {
                    "@id": "https://lifepal.co.id/#organization-logo"
                  },
                  "telephone": "+6282330030027",
                  "email": "support@lifepal.co.id",
                  "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "CIBIS 9 17th Floor, Jl. TB Simatupang No.2, Cilandak Timur, Pasar Minggu",
                    "addressLocality": "Jakarta Selatan",
                    "addressRegion": "DKI Jakarta",
                    "postalCode": "12560",
                    "addressCountry": "ID"
                  },
                  "contactPoint": {
                    "@type": "ContactPoint",
                    "contactType": "customer support",
                    "telephone": "+6282330030027",
                    "email": "support@lifepal.co.id",
                    "availableLanguage": "id",
                    "areaServed": "ID"
                  }, 
                  "sameAs": [
                    "https://www.linkedin.com/company/lifepalcoid/",
                    "https://twitter.com/lifepal",
                    "https://www.facebook.com/lifepal",
                    "https://www.youtube.com/c/@lifepalmedia",
                    "https://www.instagram.com/lifepal/",
                    "https://id.wikipedia.org/wiki/Lifepal"
                  ], 
                  "areaServed": "ID",
                  "knowsAbout": [
                    "Asuransi Mobil",
                    "Asuransi Kesehatan",
                    "Asuransi Karyawan"
                  ]
                }
              ]
            })
        }}
      />
      <script type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html:
            JSON.stringify({
              "@context": "https://schema.org",
              "@graph": [
                {
                  "@type": "WebPage",
                  "@id": "https://lifepal.co.id/#webpage",
                  "url": "https://lifepal.co.id/",
                  "name": "Lifepal - Marketplace Asuransi Terlengkap di Indonesia",
                  "description": "Lifepal membantu kamu membandingkan dan memilih asuransi mobil, asuransi kesehatan, dan asuransi karyawan dengan premi terbaik.",
                  "inLanguage": "id",
                  "isPartOf": {
                    "@id": "https://lifepal.co.id/#website"
                  },
                  "publisher": {
                    "@id": "https://lifepal.co.id/#organization"
                  },
                  "about": [
                    {
                      "@type": "Thing",
                      "name": "Asuransi Mobil"
                    },
                    {
                      "@type": "Thing",
                      "name": "Asuransi Kesehatan"
                    },
                    {
                      "@type": "Thing",
                      "name": "Asuransi Karyawan"
                    }
                  ]
                }
              ]
            })
        }}
      />
    </Head>
  )
};

Meta.propTypes = {
  title: PropTypes.string.isRequired,
  isPayment: PropTypes.bool,
  meta: PropTypes.node
};

Meta.defaultProps = {
  title: 'Default Title'
};

export default Meta;
